export function validateDomReady(): void {
  const rootElement = document.getElementById("root");

  if (!rootElement) {
    throw new Error(
      'Root element with id "root" not found. ' +
        'Please ensure your HTML template contains <div id="root"></div>',
    );
  }

  // Check if root element is empty (good practice)
  if (rootElement.hasChildNodes()) {
    console.warn(
      "Root element contains existing content. " +
        "This may cause hydration issues in production.",
    );
  }
}

export function validateBrowserSupport(): void {
  // Check for required browser features
  const requiredFeatures = [
    { name: "Promise", test: () => typeof Promise !== "undefined" },
    { name: "fetch", test: () => typeof fetch !== "undefined" },
    {
      name: "localStorage",
      test: () => {
        try {
          localStorage.setItem("test", "test");
          localStorage.removeItem("test");
          return true;
        } catch {
          return false;
        }
      },
    },
    {
      name: "sessionStorage",
      test: () => {
        try {
          sessionStorage.setItem("test", "test");
          sessionStorage.removeItem("test");
          return true;
        } catch {
          return false;
        }
      },
    },
  ];

  const missingFeatures = requiredFeatures
    .filter((feature) => !feature.test())
    .map((feature) => feature.name);

  if (missingFeatures.length > 0) {
    throw new Error(
      `Browser compatibility check failed. Missing features: ${missingFeatures.join(", ")}. ` +
        "Please update your browser to a more recent version.",
    );
  }
}

export function setupGlobalErrorHandlers(): void {
  // Store original console methods to avoid suppression
  const originalError = console.error;
  const originalWarn = console.warn;

  // Check if we're in development mode safely
  const isDev =
    typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.DEV;

  // Enhanced error logging for development
  if (isDev) {
    console.error = (...args: any[]) => {
      try {
        // Check for React hydration warnings and highlight them
        const message = args[0]?.toString() || "";
        if (message.includes("Hydration") || message.includes("hydration")) {
          console.group("⚠️ HYDRATION WARNING");
          originalError.apply(console, args);
          console.groupEnd();
        } else {
          originalError.apply(console, args);
        }
      } catch (e) {
        // Fallback to original if anything goes wrong
        originalError.apply(console, args);
      }
    };

    console.warn = (...args: any[]) => {
      try {
        const message = args[0]?.toString() || "";
        if (message.includes("Hydration") || message.includes("hydration")) {
          console.group("⚠️ HYDRATION WARNING");
          originalWarn.apply(console, args);
          console.groupEnd();
        } else {
          originalWarn.apply(console, args);
        }
      } catch (e) {
        // Fallback to original if anything goes wrong
        originalWarn.apply(console, args);
      }
    };
  }

  // Handle unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    try {
      console.group("🚨 Unhandled Promise Rejection");
      originalError.call(console, "Reason:", event.reason);
      originalError.call(console, "Promise:", event.promise);
      console.groupEnd();
    } catch (e) {
      originalError.call(console, "Unhandled Promise Rejection:", event.reason);
    }
    // Prevent default to avoid "Uncaught (in promise)" in console
    event.preventDefault();
  });

  // Handle uncaught errors
  window.addEventListener("error", (event) => {
    try {
      // Skip React internal errors that are already handled by ErrorBoundary
      if (event.error?.message?.includes("Error caught by boundary")) {
        return;
      }

      console.group("🚨 Uncaught Error");
      originalError.call(console, "Message:", event.message);
      originalError.call(console, "Error:", event.error);
      originalError.call(console, "File:", event.filename);
      originalError.call(
        console,
        "Line:",
        event.lineno,
        "Column:",
        event.colno,
      );
      console.groupEnd();
    } catch (e) {
      originalError.call(console, "Uncaught error:", event.error);
    }
  });

  // Handle React errors specifically in development
  if (isDev) {
    window.addEventListener("error", (event) => {
      try {
        if (event.error && event.error.message?.includes("React")) {
          console.group("⚛️ React Error");
          originalError.call(
            console,
            "React error caught globally:",
            event.error,
          );
          originalError.call(console, "Stack:", event.error.stack);
          console.groupEnd();
        }
      } catch (e) {
        // Silently ignore if grouping fails
      }
    });
  }
}

export function setupPerformanceMonitoring(): void {
  // Measure app startup time
  if ("performance" in window && "mark" in window.performance) {
    try {
      performance.mark("app-start");
    } catch (error) {
      console.warn("Performance monitoring not fully supported:", error);
    }
  }
}
